import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import '../DataEntryDetails.scss';
import IconWithButton from '../../../common/IconWithButton/IconWithButton';
import UploadFileModal, { DocumentUploadSubmission } from '../../../common/modal/UploadFileModal';
import UploadProgressBars from '../../../common/uploadProgressBars/UploadProgressBars';
import RecordsTable, { AuxDocumentErrors } from '../../../common/table/RecordsTable';
import { documentURL } from '../../../services/Network';
import MedicalRecordsRetrieve, {
    MedDocumentErrors
} from '../../../common/table/medicalRecordsRetrieve/MedicalRecordsRetrieve';
import ButtonWithDropdown from '../../../common/buttonWithDropdown/ButtonWithDropdown';
import CopyTransactionModal from '../../../common/modal/CopyTransactionModal';
import { connect } from "react-redux";
import { DocTypes } from '../../../common/constants/DocTypes';
import { getReadOnlyDocumentName, NO_FILE_NAME } from '../../../common/header/getReadOnlyDocumentName';
import {
    batchUpdateDocumentsActionCreator,
    getAuditTrail,
    postWPSToken,
    readBarCodeDDSRequest,
    resetTransactionDocumentsToCopy,
    updateReplaceDocucmentActionCreator,
    userViewedDocumentRequest
} from '../DataEntryDetailsDucks';
import { requestIntakeIntelligenceActionCreator } from '../IntakeDucks';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Button } from "react-bootstrap";
import moment from 'moment';
import LoadingSpinner from "../../../common/loadingSpinner/loadingSpinner";
import { API_FETCHING, API_SUCCESS, INVALID_DATE, ReduxApiStatus } from '../../../common/constants/ApiConstants';
import PdfViewer from '../../../common/pdfViewer/PdfViewer';
import ReleaseDocumentMissingPopUp from '../../../common/alertPopup/ReleaseDocumentMissingPopUp';
import TransactionDocument, {
    DocumentUpdateRequest,
    DocumentUpdateRequests,
    DocumentUploadRequest,
    ModifiableValue,
} from '../../../models/document';
import { DISABILITY_OR_SOCIAL_SECURITY } from '../../../models/requestType';
import { TransactionDetails } from '../../../models/transaction';
import { DropdownOption } from '../../../common/formFields/DropdownField';
import editSmallIcon from "../../../assets/images/editIcons/editSmallIcon.png";
import cancelIcon from "../../../assets/images/tabImages/cancelButton.png";
import checkOutlineLargeWhite from "../../../assets/images/tabImages/CheckMark_Large_Outline_white.svg";
import toast from "react-hot-toast";
import FeeSchedule from '../../../models/feeSchedule';
import { AssessmentResult, EntityTypeId } from '../../../models/assessment';
import { FeatureFlags } from '../../login/LoginPageDucks';
import FacilityConfiguration from '../../../models/facilityConfiguration';
import AiSparkles from "../../../assets/images/ai_sparkles.png";
import { RootState } from "typesafe-actions";
import { FormStateMap } from "redux-form";
import RequestAnalysis, { RequestAnalysisStatus } from "../../../models/intake";
import { Dispatch } from "redux";
import IntakeResultsDialog from "./IntakeResultsDialog";

interface IpropType {
    activeTabId: number,
    documentsList: readonly TransactionDocument[],
    uploadMultipleDocuments: (request: DocumentUploadRequest) => void,
    transactionId: string,
    transactionDetails: TransactionDetails,
    getTransactionDocuments: Function,
    getDocumentById: (documentId: string) => void,
    updateDocument: (update: any) => void,
    documentSources: readonly DropdownOption[],
    deleteDocument: (documentId: string) => void,
    deleteAuxDocument: (documentId: string) => void,
    deleteDocumentSuccess?: boolean,
    hasUserMadeAnyChanges: boolean,
    userHasMadeChanged: Function,
    getTransactionDocumentsAPIStatus: number,
    docAIProcessID?: string,
    setPageNumber?: Function,
    pageNumber: number,
    currentFile: string,
    setCurrentFileType?: Function,
    currentFileType?: string,
    assessmentResult?: AssessmentResult,
    authorizedEntities: {readonly [key in EntityTypeId]?: boolean},
    /**
     * Bug fix (AB#34102): the "Classic Mode" toolbar button (same row as
     * Save/Attach Request/Attach Internal, per the design handoff) only
     * belongs on a Quick-Entry-eligible transaction - showing it once the
     * user is already in Classic Mode (or on a facility/flag configuration
     * that never renders Quick Entry) would offer a button to switch to the
     * mode already active. Callers pass DataEntryDetails.isQuickEntryEligible().
     */
    showClassicModeButton: boolean,
    /** Opens the "Leave Quick Entry?" dialog - same handler the old step-tabs__leave-link text link called (DataEntryDetails.onClickLeaveQuickEntry). */
    onClickLeaveQuickEntry?: () => void,
    /**
     * Quick Entry's live, not-yet-saved Request Type form value (Spec #35136 / F1f).
     * Lets the shared documentsList effect fire the DDS barcode scan on a DDS type
     * picked live before Save. Classic Mode's mount site omits it (undefined), so
     * the effect's condition collapses to its pre-existing persisted-value-only gate.
     */
    liveRequestTypeId?: number,
}

interface ReduxStateProps {
    copyTransactionComplete?: boolean,
    wpsToken?: string,
    formState: FormStateMap,
    completeTransactionFinished?: boolean,
    uploadDocumentAPIStatus: ReduxApiStatus,
    uploadDocumentFailure?: any,
    uploadDocumentSuccess?: any,
    featureFlags: FeatureFlags,
    patientDob?: any,
    feeSchedules: readonly FeeSchedule[],
    facilityConfiguration?: FacilityConfiguration,
    requestAnalysis?: RequestAnalysis,
    getIntakeIntelligenceStatus: ReduxApiStatus,
    requestIntakeIntelligenceStatus: ReduxApiStatus,
}

interface ReduxDispatchProps {
    resetTransactionDocumentsToCopy: () => void;
    postWPSToken: (transactionId: string, docType: DocTypes) => void;
    readBarCodeDDSRequest: (data: string) => void;
    getAuditTrailData: (transactionId: string) => void;
    userViewedDocumentRequest: (documentId: string) => void;
    updateReplaceDocument: (data: any) => void;
    batchUpdateDocuments: (data: DocumentUpdateRequests) => void;
    requestIntakeIntelligence: (organizationId: number, requestBufferId: number, onSuccess?: () => void) => void;
}

type Order = 'asc' | 'desc';

interface Data {
    description: string;
    source: string;
    sab: string;
    size: string;
    pageCount: number;
    actions: string;
    dateOfService: string;
}

function DataEntryDetailsBody(props: IpropType & ReduxStateProps & ReduxDispatchProps) {
    function usePrevious<T>(value: T) {
        // The ref object is a generic container whose current property is mutable ...
        // ... and can hold any value, similar to an instance property on a class
        const ref = useRef<T>();
        // Store current value in ref
        useEffect(() => {
            ref.current = value;
        }, [value]); // Only re-run if value changes
        // Return previous value (happens before update in useEffect above)
        return ref.current;
    }

    const editRowTypes: readonly DocTypes[] = [DocTypes.AUX, DocTypes.MED, DocTypes.REQ];

    const defaultDocument =
        props.documentsList.find(row => row.recType === DocTypes.QA) ??
        props.documentsList.find(row => row.recType === DocTypes.RELEASE) ??
        props.documentsList.find(row => row.recType === DocTypes.REQ || row.recType === DocTypes.INT) ??
        (props.documentsList.length > 0 ? props.documentsList[0] : null);

    const [refresh, setRefresh] = useState(false);
    const [show, setShow] = useState(false);
    const [showRecordGrid, setShowRecordGrid] = useState(true);
    const {activeTabId} = props;
    const [selectedDocumentId, setSelectedDocumentId] = useState(defaultDocument?.id);
    const [selectedMedDocumentIndex, setSelectedMedDocumentIndex] = useState("");
    const userId = global.userID;
    const [fileType, setFileType] = useState("");
    const [showCopyTransaction, setShowCopyTransaction] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);
    const [uploadProgress, setUploadProgress] = useState<any>({});
    const [showReleaseMissing, setShowReleaseMissing] = useState(false);
    const [saveTrigger, setSaveTrigger] = useState(false);
    const [documentRefresh, setDocumentRefresh] = useState<NodeJS.Timeout>();
    const prevSelectDocument = usePrevious(selectedDocumentId)
    const [editingAux, setEditingAux] = useState(false);
    const [editingMed, setEditingMed] = useState(false);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('pageCount');
    const [editRows, setEditRows] = useState<readonly TransactionDocument[]>([]);
    const [showIntakeResults, setShowIntakeResults] = useState(false);

    const prevHasUserMadeChanges = usePrevious(props.hasUserMadeAnyChanges)

    const currentDocument = props.documentsList.find(doc => doc.id === selectedDocumentId);
    const canSave = (currentDocument &&
            currentDocument.recType !== DocTypes.RELEASE &&
            currentDocument.recType !== DocTypes.QA)
            ?? false;

    const intakeLoading = props.requestIntakeIntelligenceStatus === API_FETCHING ||
            props.getIntakeIntelligenceStatus === API_FETCHING;
    const requestExists = props.documentsList.some(doc => doc.recType === DocTypes.REQ);
    const hasIntakeResults = props.requestAnalysis?.status === RequestAnalysisStatus.Completed &&
            props.requestAnalysis?.data != null;
    const tooManyIntakeTries = props.requestAnalysis?.status === RequestAnalysisStatus.RetryExceeded;
    // AB#34098 (Tech Lead note): once QuickEntry is on, the new split-panel
    // screen is the canonical place to review intake results - the old
    // Extract button/floating dialog must not also appear (whether the
    // transaction is actually rendering Quick Entry, or its Classic-Mode
    // fallback sibling). With QuickEntry off, this is byte-identical to the
    // pre-existing gate (IntakeIntelligence flag + facility toggle only).
    const intakeVisible = (props.featureFlags.IntakeIntelligence &&
            props.facilityConfiguration?.intakeIntelligenceEnabled &&
            !props.featureFlags.QuickEntry)
            ?? false;
    const intakeEnabled = intakeVisible && !intakeLoading && !tooManyIntakeTries && requestExists;
    const intakeTitle = (hasIntakeResults && "View information extracted from the request document") ||
            (intakeEnabled && "Extract information from the request document") ||
            (tooManyIntakeTries && "This request failed to extract and cannot be retried further") ||
            (!requestExists && "Requires a request document") ||
            (intakeLoading && "Please wait...") ||
            "";

    const types = [
        DocTypes.MED,
        DocTypes.REQ,
        DocTypes.INT,
        DocTypes.AUX,
        DocTypes.NOTE,
        DocTypes.RECEIPT,
        DocTypes.COVER,
        DocTypes.RELEASE,
        DocTypes.QA,
    ] as const;
    const medicalRecords = props.documentsList.filter(row => row.type.role === DocTypes.MED);
    const requestFiles = props.documentsList.filter(row => row.type.role === DocTypes.REQ);
    const internalFiles = props.documentsList.filter(row => row.type.role === DocTypes.INT);
    const auxiliaryRecords = props.documentsList.filter(row => row.type.role === DocTypes.AUX);
    const notificationFiles = props.documentsList.filter(row => row.type.role === DocTypes.NOTE || row.type.role == DocTypes.RECEIPT);
    const coverFiles = props.documentsList.filter(row => row.type.role === DocTypes.COVER);
    const releaseFiles = props.documentsList.filter(row => row.type.role === DocTypes.RELEASE || row.type.role == DocTypes.QA);
    const otherFiles = props.documentsList.filter(row => !types.includes(row.type.role));

    const annotationAllowed = currentDocument?.type.role == DocTypes.QA &&
            props.transactionDetails.aiProcessingCompleted &&
            !props.transactionDetails.facility.prohibitTraining;

    const bulkDocumentEdit = props.featureFlags?.MultiState ?? false;
    const auxDocumentsToEdit = props.documentsList?.filter(doc => doc.recType === DocTypes.AUX)?.length > 0 ?? false;
    const medDocumentsToEdit = props.documentsList?.filter(doc => doc.recType === DocTypes.MED)?.length > 0 ?? false;

    const showModal = (fileType: DocTypes) => {
        setFileType(fileType);
        setRefresh(false);
        setShow(true);
    }

    const docTypes = {
        REQ: "request document",
        INT: "internal records",
        AUX: "auxiliary document",
        MED: "medical records",
    } as const

    const getIframeKey = () => {
        let newKey: number;
        do {
            newKey = Math.floor(Math.random() * (999999 - 100000) + 100000);
        } while (newKey === iframeKey)
        return newKey;
    }

    useEffect(() => {
        if (props.currentFile) {
            let record = props.documentsList.find((row => row.description === props.currentFile));
            if (record === undefined) {
                record = props.documentsList.find((row => row.file.name.includes(props.currentFile)));
            }
            setSelectedDocumentId(record?.id);
        }
    }, [props.currentFile])

    useEffect(() => {
        if (selectedDocumentId) {
            let record = props.documentsList.find((row => row.id === selectedDocumentId));
            if (record === undefined) {
                record = props.documentsList.find((row => row.file.name.includes(props.currentFile)));
            }
            if (props.setCurrentFileType) {
                props.setCurrentFileType(record?.type?.role);
            }
        }
    }, [selectedDocumentId])

    useEffect(() => {
        const id = props.documentsList.some(d => d.id === selectedDocumentId) ? selectedDocumentId : defaultDocument?.id;
        if (id) {
            if (id != selectedDocumentId) {
                setIframeKey(getIframeKey());
            }
            setSelectedDocumentId(id);
        }

        setEditRows(props.documentsList.filter(document => editRowTypes.includes(document.recType)));

        const reqDoc = props.documentsList.find(el => el.id.includes(DocTypes.REQ))
        // Spec #35136 (F1f), Business Rule 1+2: Classic gates ONLY on the PERSISTED
        // request type, which is reliably in sync by the time its tabbed form reaches
        // the DDS tab. Quick Entry is single-screen and can have "DDS just picked, not
        // yet saved" as a live state this shared effect can't otherwise see, so ALSO
        // accept the live in-progress form value (liveRequestTypeId). Classic's own
        // mount site omits liveRequestTypeId (undefined), so its behavior is unchanged.
        if (reqDoc && (props.transactionDetails.requestPurpose?.typeID == DISABILITY_OR_SOCIAL_SECURITY
            || props.liveRequestTypeId == DISABILITY_OR_SOCIAL_SECURITY)) {
            props.readBarCodeDDSRequest && props.readBarCodeDDSRequest(reqDoc.id);
        }
    }, [props.documentsList]);

    useEffect(() => {
        if (selectedDocumentId && (props.hasUserMadeAnyChanges != prevHasUserMadeChanges) && (selectedDocumentId == prevSelectDocument)) {
            props.userViewedDocumentRequest && props.userViewedDocumentRequest(selectedDocumentId)
        }
    }, [props.hasUserMadeAnyChanges])


    const refreshDocuments = useCallback(() => {
        props.getTransactionDocuments(props.transactionId);
    }, [props.getTransactionDocuments, props.transactionId]);


    const hideModal = () => {
        setShow(false)
        const id = props.documentsList.find(row => row.type.role === DocTypes.REQ || row.type.role === DocTypes.INT)?.id;
        if (selectedDocumentId === id) {
            setSelectedDocumentId("-1")
            setSelectedDocumentId(selectedDocumentId)
        }
    }

    useEffect(() => {
        if (props.uploadDocumentSuccess && uploadProgress && Object.keys(uploadProgress).length && Object.keys(uploadProgress).every(f => uploadProgress[f].loaded === 100)) {
            setUploadProgress(prev => ({}));
            refreshDocuments();
            setRefresh(true);
        }
    }, [props.uploadDocumentSuccess?.fileID])

    const UploadProgressCreator = (fileId, filename) => (progress) => {
        let percentage = Math.floor((progress.loaded * 100) / progress.total)
        setUploadProgress(prev => ({
            ...prev,
            [fileId]: {
                name: filename,
                loaded: percentage
            }
        }));
    }

    const uploadFiles = (files: { [id in string]: DocumentUploadSubmission }, append: boolean) => {
        hideModal();
        const requestObjs: DocumentUploadRequest[] = [];
        const initProgress = {};
        for (const fileKey of Object.keys(files)) {
            const requestObject: DocumentUploadRequest = {
                fileName: files[fileKey].name,
                fileID: fileKey,
                recordType: files[fileKey]["fileType"],
                file: files[fileKey].file,
                transactionId: props.transactionId,
                state: files[fileKey].state,
                append: append,
                uploadProgress: UploadProgressCreator(fileKey, files[fileKey].name),
                // Same value that gates the "Classic Mode" toolbar button (showClassicModeButton
                // = DataEntryDetails.isQuickEntryEligible()) - reused here so the auto-extract
                // trigger on upload only fires when Quick Entry is actually the active mode.
                quickEntryActive: props.showClassicModeButton,
            }

            initProgress[fileKey] = { name: files[fileKey].name, loaded: 0 };

            requestObjs.push(requestObject);
        }

        setUploadProgress(prev => initProgress);
        process.nextTick(() => {
            for (const o of requestObjs) {
                props.uploadMultipleDocuments(o);
                if (o.recordType === DocTypes.REQ) {
                    setShowIntakeResults(false);
                }
            }
        });
    }

    useEffect(() => {
        if (props.transactionDetails.transactionStatus?.id >= 130 &&
            props.transactionDetails.transactionStatus?.id <= 141) {
            const isApiSuccess = props.getTransactionDocumentsAPIStatus === API_SUCCESS;
            const releaseDocs = isApiSuccess && props.documentsList.some(row => row.recType == DocTypes.RELEASE || row.recType == DocTypes.QA);
            setShowReleaseMissing(isApiSuccess && !releaseDocs);
        }
    }, [props.transactionDetails?.transactionStatus, props.getTransactionDocumentsAPIStatus]);

    // AB#35069 / Bug 31938: show the real filename (not the "Request"/"Affidavit"/"Invoice"
    // label) via the shared resolver, so this view and the read-only header agree.
    const getFileName = (file: TransactionDocument) => {
        const name = getReadOnlyDocumentName(file);
        return name === NO_FILE_NAME ? "No file name" : name;
    }

    const addRecordsSelect = (eventKey) => {
        if (eventKey.id === "a") {
            showModal(DocTypes.MED);
        } else if (eventKey.id === "c") {
            showCopyModal();
        } else if (eventKey.id === "w") {
            props.postWPSToken && props.postWPSToken(props.transactionId, DocTypes.MED);
        }
    }

    const attachAuxSelect = (eventKey) => {
        if (eventKey.id === "a") {
            showModal(DocTypes.AUX);
        } else if (eventKey.id === "w") {
            props.postWPSToken && props.postWPSToken(props.transactionId, DocTypes.AUX);
        }
    }

    const attachInternalSelect = (eventKey) => {
        if (eventKey.id === "a") {
            showModal(DocTypes.INT);
        } else if (eventKey.id === "w") {
            props.postWPSToken && props.postWPSToken(props.transactionId, DocTypes.INT);
        }
    }

    const attachRequestSelect = (eventKey) => {
        if (eventKey.id === "a") {
            showModal(DocTypes.REQ);
        } else if (eventKey.id === "w") {
            props.postWPSToken && props.postWPSToken(props.transactionId, DocTypes.REQ);
        }
    }

    useEffect(() => {
        if (props.wpsToken && props.wpsToken != "") {
            window.open("verismawps:" + props.wpsToken, '_blank');
            const interval = setInterval(() => {
                refreshDocuments();
            }, 5000)

            setDocumentRefresh(interval);

            return () => clearInterval(interval);
        }
    }, [props.wpsToken]);

    useEffect(() => {
        if (documentRefresh) {
            clearInterval(documentRefresh);
        }
    }, [activeTabId]);

    const showCopyModal = () => {
        props.resetTransactionDocumentsToCopy && props.resetTransactionDocumentsToCopy();
        setShowCopyTransaction(true);
    }

    const hideCopyModal = () => {
        setShowCopyTransaction(false);
        props.resetTransactionDocumentsToCopy && props.resetTransactionDocumentsToCopy();
    }

    useEffect(() => {
        if (props.copyTransactionComplete) {
            setShowCopyTransaction(false);
        }
    }, [props.copyTransactionComplete]);

    const changeSubRowData = (documentId: string, value: string, key: ModifiableValue) => {
        const newItem = { ...editRows.find(document => document.id == documentId)! };

        if (key == ModifiableValue.FeeSchedule) {
            setEditRows(editRows.map(document => {
                if (document.state == newItem.state) {
                    return { ...document, feeScheduleId: parseInt(value) };
                } else {
                    return document;
                }
            }));
            return;
        }

        switch (key) {
            case ModifiableValue.Account:
                newItem.account = value;
                break;
            case ModifiableValue.To:
                newItem.dateToService = value && value != INVALID_DATE ? new Date(value) : undefined;
                break;
            case ModifiableValue.From:
                newItem.dateOfService = value && value != INVALID_DATE ? new Date(value) : undefined;
                break;
            case ModifiableValue.Description:
                newItem.description = value;
                break;
            case ModifiableValue.Source:
                newItem.sourceID = Number(value);
                break;
            case ModifiableValue.State:
                newItem.state = value;
                const newFee = editRows.find(document => document.state == newItem.state)?.feeScheduleId;

                if (newFee) {
                    newItem.feeScheduleId = newFee;
                } else {
                    const state = props.feeSchedules?.find(fee => fee.id == newItem.feeScheduleId)?.state;
                    if (state && state != value) {
                        newItem.feeScheduleId = undefined;
                    }
                }

                break;
            default:
                throw new Error("Unsupported changeSubRowData");
        }

        setEditRows(editRows.map(document => document.id == documentId ? newItem : document));
    }

    const medDocumentData = (): (TransactionDocument & { filePath: string }) | undefined => {
        const document = props.documentsList.find(d => d.id === selectedMedDocumentIndex);
        if (document) {
            return {
                ...document,
                filePath: documentURL + selectedMedDocumentIndex + "/file?userid=" + userId + (document.type.role === DocTypes.MED ? "" : "&addTrailEntry=false"),
            };
        }
    }

    const documentData = (): (TransactionDocument & { filePath: string }) | undefined => {
        const document = props.documentsList.find(d => d.id === selectedDocumentId)
        if (document) {
            return {
                ...document,
                filePath: documentURL + selectedDocumentId + "/file?addTrailEntry=false&userid=" + userId + '&viewedDocument=' + props.hasUserMadeAnyChanges.toString()
            }
        }
    }

    const recordGridState = (state: boolean) => {
        if (!state) {
            setSelectedMedDocumentIndex('')
        }
        setShowRecordGrid(state);
    }

    useEffect(() => {
        if (saveTrigger) {
            setSaveTrigger(false);
        }
    }, [saveTrigger])

    const onClickSave = () => {
        setSaveTrigger(true);
    }

    useEffect(() => {
        if (props.deleteDocumentSuccess) {
            setSelectedDocumentId(undefined)
            refreshDocuments();
        }
    }, [props.deleteDocumentSuccess])

    const saveFile = (fileData: TransactionDocument & { blobData: Blob, pagesDeleted: number }) => {
        const date = new Date();
        const id = fileData.id.split('-').slice(-1)[0];

        let requestObject = {
            fileName: fileData.file.name,
            fileID: fileData.file.name + "-0-" + (new Date()).getTime() + Math.random(),
            recordType: fileData.type.role,
            file: {
                path: fileData.file.name,
                lastModified: date.getTime(),
                lastModifiedDate: date,
                name: fileData.file.name,
                size: fileData.blobData.size,
                type: fileData.blobData.type,
                webkitRelativePath: "",
            },
            documentId: fileData.id,
            transactionId: props.transactionId,
            blob: fileData.blobData,
            pagesRemoved: fileData.pagesDeleted,
            pagesAppended: undefined,
            uploadProgress: UploadProgressCreator(fileData.file.name + '.' + date.getTime().toString() + '.' + id, fileData.file.name)
        }

        process.nextTick(() => {
            props.updateReplaceDocument && props.updateReplaceDocument(requestObject)
            recordGridState(true);
        })

    }

    const [showReplaceAppend, setShowReplaceAppend] = useState(false);

    useEffect(() => {
        setShowReplaceAppend(props.documentsList.some(d => d.type.role === fileType))
    }, [props.documentsList, fileType])

    const deleteFile = useEvent((deleteAllPages: boolean, removePages: Function) => {
        if (selectedDocumentId) {
            const fileType = props.documentsList.find(doc => doc.id == selectedDocumentId)?.type.role;
            if (deleteAllPages) {
                if (fileType === DocTypes.INT) {
                    if (window.confirm("Deleting the last page of the document will delete the file and can not be undone. Are you sure you wish to continue?")) {
                        props.deleteAuxDocument(selectedDocumentId);
                        setSelectedDocumentId(undefined);
                    }
                } else {
                    window.alert("You cannot delete all the pages in a Request document.")
                }
            } else {
                removePages();
            }
        }
    });

    function resetPageNumber(selectedDocumentID: string) {
        const record = props.documentsList.find(row => row.id === selectedDocumentID);
        const fileName = record && getFileName(record)

        if (fileName && props.setPageNumber) {
            props.setPageNumber(null, fileName);
        } else {
            console.warn("resetPageNumber: Could not find file name for document ID:", selectedDocumentID);
        }
    }

    function stableSort<T>(array: Array<T>, order: Order, orderBy: string) {
        const compare = (a: T, b: T) => {
            return (a[orderBy] || "").toString().localeCompare(b[orderBy]);
        }
        if (order === "asc") {
            return array.sort(compare)
        } else {
            return array.sort(compare).reverse()
        }
    }

    const accountAllowed = "ABCDEFGHIJKLMNOPQURSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890-.";

    const billingStates = (props.transactionDetails?.facility?.billingStates?.length ?? 0) > 1 ?
        props.transactionDetails!.facility.billingStates : [];

    const isMultiState = billingStates.length > 1;

    const errorsMed: readonly MedDocumentErrors[] = editRows.filter(document => document.recType == DocTypes.MED).map(row => {
        let fromError = "";
        let today = new Date();
        if (row.dateOfService != null) {
            const date = moment(row.dateOfService).startOf('day').toDate();
            const maxdate = row.dateToService ?? today;
            if (date > today) {
                fromError = "The From date can not be greater than the current date.";
            } else if (date > maxdate) {
                fromError = "The From date can not be greater than the To date or the current date";
            } else if (date < moment(props.patientDob).toDate()) {
                fromError = "The From date can not be earlier than the patient DoB";
            }
        }
        let toError = "";
        if (row.dateToService != null) {
            const date = moment(row.dateToService).startOf('day').toDate();
            const mindate = row.dateOfService ?? moment(props.patientDob).toDate();
            if (date > today) {
                toError = "The To date can not be greater than the current date.";
            } else if (date < mindate) {
                toError = "The To date can not be earlier than the From date or the patient DoB";
            }
        }
        return new MedDocumentErrors(
            fromError,
            toError,
            [...((row.account ?? "") as any)].some(c => !accountAllowed.includes(c))
                ? "Only letters, numbers, hyphen and period characters allowed" : "",
            (row.state || !isMultiState) ? "" : "Select a state.",
            (row.feeScheduleId || !isMultiState) ? "" : "Select a fee schedule.",
        )
    });

    const errorsAux: readonly AuxDocumentErrors[] = editRows
        .filter(document => document.recType == DocTypes.AUX)
        .map(row => new AuxDocumentErrors(
            (row.feeScheduleId || !isMultiState) ? "" : "Select a fee schedule.",
            (row.state || !isMultiState) ? "" : "Select a state.",
        ));

    const showFeeScheduleUpdate = (messages: { [state: string]: number } ) => {
        Object.entries(messages).forEach(([state, count]) => {
            toast.success(
                <div>
                    <div className='upload-toast-file'>Fee Schedule Update</div>
                    <div className='upload-toast-progress'>
                        Fee schedules for {count} document{count !== 1 ? 's' : ''} for {state} were updated.
                    </div>
                </div>,
                {
                    className: "upload-toast",
                    duration: 5000,
                }
            );
        });
    }

    function saveDocuments(changedRows: readonly TransactionDocument[]) {
        const feeScheduleChanges = changedRows.filter(row => {
            const original = props.documentsList.find(d => d.id === row.id);
            return original && (original.feeScheduleId !== row.feeScheduleId || original.state !== row.state);
        });

        const messages: { [state: string]: number } = {};

        feeScheduleChanges.forEach(row => {
            const state = row.state ?? ""; // fallback to empty string if null/undefined
            messages[state] = (messages[state] || 0) + 1;
        });

        showFeeScheduleUpdate(messages);

        const updateDocs: DocumentUpdateRequest[] = changedRows.map(doc => ({
            documentId: doc.id,
            fileDescription: doc.description,
            recordSourceId: doc.sourceID ?? 0,
            fromDateOfService: doc.dateOfService,
            toDateOfService: doc.dateToService,
            account: doc.account,
            feeScheduleId: doc.feeScheduleId,
            state: doc.state
        }));
        const updateRequests: DocumentUpdateRequests = {
            updates: updateDocs,
            transactionId: props.transactionId,
            userId: global.userID,
        };
        props.batchUpdateDocuments?.(updateRequests);
    }

    const onClickEditAllDocs = () => {
        const newEditRows = props.documentsList.map(document => ({ ...document }));
        if (newEditRows) {
            stableSort(newEditRows, order, orderBy);
            setEditRows(newEditRows);
        }

        setEditingAux(true);
        setEditingMed(true);
    }

    const onClickCancelAllDocs = () => {
        setEditingAux(false);
        setEditingMed(false);
        setEditRows([]);
    }

    const onClickSaveAllDocs = () => {
        const changedRows: TransactionDocument[] = editRows.filter(document => editRowTypes.includes(document.recType))

        if (([...errorsMed, ...errorsAux]).every(error => error.isValid())) {
            saveDocuments(changedRows);
            setEditingAux(false);
            setEditingMed(false);
            setEditRows([]);
        }
    }

    function onClickEditMed() {
        setEditingMed(true);
        setEditRows([
            ...editRows.filter(row => row.recType != DocTypes.MED),
            ...props.documentsList.filter(row => row.recType == DocTypes.MED),
        ]);
    }

    function onClickCancelMedEdit() {
        setEditingMed(false);
        setEditRows(editRows.filter(row => row.recType != DocTypes.MED));
    }

    function onClickSaveMedEdit() {
        if (errorsMed.every(error => error.isValid())) {
            saveDocuments(editRows.filter(row => row.recType == DocTypes.MED));
            setEditingMed(false);
            setEditRows(editRows.filter(row => row.recType != DocTypes.MED));
        }
    }

    function onClickEditAux() {
        setEditingAux(true);
        setEditRows([
            ...editRows.filter(row => row.recType != DocTypes.AUX),
            ...props.documentsList.filter(row => row.recType == DocTypes.AUX),
        ]);
    }

    function onClickCancelAuxEdit() {
        setEditingAux(false);
        setEditRows(editRows.filter(row => row.recType != DocTypes.AUX));
    }

    function onClickSaveAuxEdit() {
        if (errorsAux.every(error => error.isValid())) {
            saveDocuments(editRows.filter(row => row.recType == DocTypes.AUX));
            setEditingAux(false);
            setEditRows(editRows.filter(row => row.recType != DocTypes.AUX));
        }
    }

    function onClickExtract(): void {
        if (hasIntakeResults) {
            setShowIntakeResults(true);
        } else {
            props.requestIntakeIntelligence(
                props.transactionDetails.facility.id,
                props.transactionDetails.id,
                () => setShowIntakeResults(true),
            );
        }
    }

    return (
        <div className='records-interface'>
            <UploadProgressBars uploadProgress={uploadProgress} uploadDocumentFailure={props.uploadDocumentFailure}
                                setUploadProgress={setUploadProgress}/>
            {activeTabId !== 4 ?
                <div className="request-pdf-wrapper">
                    <div className="viewer-files-list">
                        <span className='upload-items'>
                            <select value={selectedDocumentId} onChange={(event) => { resetPageNumber(event.target.value); props.userHasMadeChanged(); }} className="form-control">
                                {auxiliaryRecords.length && <optgroup label="Auxiliary Records">
                                    {auxiliaryRecords.map((file, index) => <option
                                        key={`aux${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {coverFiles.length && <optgroup label="Cover Sheet">
                                    {coverFiles.map((file, index) => <option
                                        key={`cover${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {requestFiles.length && <optgroup label="Request file">
                                    {requestFiles.map((file, index) => <option
                                        key={`req${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {internalFiles.length && <optgroup label="Internal files">
                                    {internalFiles.map((file, index) => <option
                                        key={`int${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {medicalRecords.length && <optgroup label="Medical Records">
                                    {medicalRecords.map((file, index) => <option
                                        key={`med${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {notificationFiles.length && <optgroup label="Notifications">
                                    {notificationFiles.map((file, index) => <option
                                        key={`not${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {releaseFiles.length && <optgroup label="Release files">
                                    {releaseFiles.map((file, index) => <option
                                        key={`release${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                                {otherFiles.length && <optgroup label="Other">
                                    {otherFiles.map((file, index) => <option
                                        key={`other${index}`}
                                        value={file.id}
                                    >
                                        {getFileName(file)}
                                    </option>)}
                                </optgroup>}
                            </select>
                        </span>
                        <div className='attach-dropdown-container'>
                            <IconWithButton
                                buttonText={'Save'}
                                onButtonClick={onClickSave}
                                hideArrowIcon={true}
                                isSave={true}
                                isDisabled={!canSave}
                                title={canSave ? "" : "Changes must be made to original documents\\files"}
                            />
                            <span className='upload attach-records dropup'>
                                <ButtonWithDropdown
                                    drop='down'
                                    title={"Attach Request"}
                                    onSelect={(event) => {
                                        attachRequestSelect(event);
                                        props.userHasMadeChanged();
                                    }}
                                    items={[
                                        {title: 'Attach Files', id: 'a'},
                                        {title: 'Web print / scan', id: 'w'}]}
                                ></ButtonWithDropdown>
                            </span>

                            <span className='upload attach-records dropup'>
                                <ButtonWithDropdown
                                    drop='down'
                                    title={"Attach Internal"}
                                    onSelect={(event) => {
                                        attachInternalSelect(event);
                                        props.userHasMadeChanged();
                                    }}
                                    items={[
                                        {title: 'Attach Files', id: 'a'},
                                        {title: 'Web print / scan', id: 'w'}]}
                                ></ButtonWithDropdown>
                            </span>
                            {intakeVisible && <span>
                                <button
                                    className="btn btn-gradient"
                                    onClick={_event => onClickExtract()}
                                    disabled={!intakeEnabled}
                                    title={intakeTitle}
                                >
                                    {intakeLoading ?
                                        <span className="spinner-border"></span>
                                        :
                                        <img src={AiSparkles} alt="" draggable="false" />
                                    }
                                    {hasIntakeResults ? "View Results" : "Extract"}
                                </button>
                            </span>}
                            {props.showClassicModeButton && (
                                <IconWithButton
                                    buttonText={'Classic Mode'}
                                    className='classic-mode-btn'
                                    onButtonClick={props.onClickLeaveQuickEntry}
                                />
                            )}
                        </div>
                    </div>
                    {userId && <PdfViewer
                        key={iframeKey}
                        saveTrigger={saveTrigger}
                        mergeTrigger={false}
                        fileData={documentData()}
                        getFileData={saveFile}
                        deleteFile={deleteFile}
                        canSave={canSave}
                        transactionId={props.transactionId}
                        refresh={refresh}
                        assessmentResult={currentDocument?.type.role == DocTypes.QA ? props.assessmentResult : undefined}
                        currentFile={props.currentFile}
                        pageNumber={props.pageNumber}
                        setPageNumber={props.setPageNumber}
                        annotationAllowed={annotationAllowed}
                        getAuditTrailData={props.getAuditTrailData!}
                        authorizedEntities={props.authorizedEntities}
                    />}
                    {show && <UploadFileModal
                        onClickSave={uploadFiles}
                        type={docTypes[fileType]}
                        handleClose={hideModal}
                        showModal={show}
                        allowAppend={showReplaceAppend}
                        states={props.transactionDetails.facility.billingStates}
                        defaultState={props.transactionDetails.facility.address.state}
                    />}
                </div>
                :
                showRecordGrid ?
                    <div className='tab-records'>
                        <div
                            className='container'
                            style={{ paddingRight: "0rem", marginRight: "0rem", marginTop: "1.5rem" }}
                        >
                            <div className="d-flex flex-row justify-content-end" style={{gap: "20px", height: "35px"}}>
                                {(editingAux || editingMed) ? <>
                                    {bulkDocumentEdit && (auxDocumentsToEdit || medDocumentsToEdit) && <>
                                        <button type="button" onClick={onClickCancelAllDocs}>
                                            <img src={cancelIcon} alt="" style={{ marginRight: "5px" }} />
                                            {/* Colour comes from .themed-muted
                                                (DataEntryDetails.scss). It was inline
                                                #6D6F72 -- the light-surface grey -- which
                                                no dark rule could override: 2.51:1 on
                                                #2c3439, 2.93:1 on #272829. */}
                                            <span className="themed-muted" style={{
                                                textDecoration: "underline",
                                                fontWeight: "600",
                                                fontSize: "12px",
                                                lineHeight: "12px",
                                            }}>
                                                Cancel
                                            </span>
                                        </button>
                                        <button type="button" className="btn" onClick={onClickSaveAllDocs}>
                                            <img src={checkOutlineLargeWhite} />
                                            Save Changes
                                        </button>
                                    </>}
                                </>
                                :
                                <>
                                    <Button
                                        style={{ paddingRight: '.75rem', width: 'fit-content', display: 'flex' }}
                                        onClick={refreshDocuments}
                                    >
                                        <RefreshIcon className="refresh-docs-icon" style={{marginRight: 4}} />
                                        Refresh Document Lists
                                    </Button>
                                    {bulkDocumentEdit && (auxDocumentsToEdit || medDocumentsToEdit) && <Button
                                        style={{ paddingRight: '.75rem', width: 'fit-content', display: 'flex' }}
                                        onClick={onClickEditAllDocs}
                                    >
                                        <img src={editSmallIcon} alt="Edit Icon"/>
                                        Edit Documents
                                    </Button>}
                                </>}
                            </div>
                        </div>

                        <div className='aux-document-container record-container'>
                            <div className="aux-document-header-container record-header-container">
                                <span className='aux-document-header'>Auxiliary Document</span>
                                {editingAux ?
                                    <>{!bulkDocumentEdit && auxDocumentsToEdit && <span className="record-dropdown-container">
                                        <button
                                            type="button"
                                            className="cancel-button"
                                            onClick={onClickCancelAuxEdit}
                                        >
                                            <img src={cancelIcon} alt="" />
                                            <span>Cancel</span>
                                        </button>
                                        <button type="button" className="btn" onClick={onClickSaveAuxEdit}>
                                            <img src={checkOutlineLargeWhite} />
                                            Save Changes
                                        </button>
                                    </span>}</>
                                    :
                                    <span className="record-dropdown-container">
                                        {!bulkDocumentEdit && auxDocumentsToEdit && <button
                                            className="edit-documents"
                                            onClick={onClickEditAux}
                                        >
                                            <img src={editSmallIcon} alt="" />
                                            <span>Edit</span>
                                        </button>}
                                        <ButtonWithDropdown
                                            title={"Attach Auxiliary"}
                                            drop='down'
                                            onSelect={(event) => attachAuxSelect(event)}
                                            items={[
                                                { title: 'Attach Files', id: 'a' },
                                                { title: 'Web print / scan', id: 'w' },
                                            ]}
                                        />
                                        {show && <UploadFileModal
                                            tab="record"
                                            onClickSave={uploadFiles}
                                            handleClose={hideModal}
                                            type={docTypes[fileType]}
                                            showModal={show}
                                            allowAppend={showReplaceAppend}
                                            states={props.transactionDetails.facility.billingStates}
                                            defaultState={props.transactionDetails.facility.address.state}
                                        />}
                                    </span>
                                }
                            </div>
                            <RecordsTable
                                rows={(editingAux ? editRows : props.documentsList).filter(document => document.recType == DocTypes.AUX)}
                                transactionId={props.transactionId}
                                getTransactionDocuments={refreshDocuments}
                                updateDocument={props.updateDocument}
                                deleteDocument={props.deleteAuxDocument}
                                hideSource={true}
                                getDocumentById={props.getDocumentById}
                                setShowRecordGrid={setShowRecordGrid}
                                setSelectedMedDocumentIndex={setSelectedMedDocumentIndex}
                                auditOnShowDoc={false}
                                isEditing={editingAux}
                                changeSubRowData={changeSubRowData}
                                errors={errorsAux}
                                billingStates={billingStates}
                                isMultiState={isMultiState}
                            />
                        </div>

                        <div className='medrec-container record-container'>
                            <div className="medrec-header-container record-header-container">
                                <span className='medrec-header record-header'>
                                    {medicalRecords.length} Medical Record{medicalRecords.length === 1 ? "" : "s"} Retrieved
                                </span>
                                {editingMed ?
                                    <>{!bulkDocumentEdit && medDocumentsToEdit && <span className="record-dropdown-container">
                                        <button
                                            type="button"
                                            className="cancel-button"
                                            onClick={onClickCancelMedEdit}
                                        >
                                            <img src={cancelIcon} alt="" />
                                            <span>Cancel</span>
                                        </button>
                                        <button type="button" className="btn" onClick={onClickSaveMedEdit}>
                                            <img src={checkOutlineLargeWhite} />
                                            Save Changes
                                        </button>
                                    </span>}</>
                                    :
                                    <span className="record-dropdown-container">
                                        {!bulkDocumentEdit && medDocumentsToEdit && <button
                                            className="edit-documents"
                                            onClick={onClickEditMed}
                                        >
                                            <img src={editSmallIcon} alt="" />
                                            <span>Edit</span>
                                        </button>}
                                        <ButtonWithDropdown
                                            drop='down'
                                            onSelect={(event) => addRecordsSelect(event)}
                                            title={"Add Records"}
                                            items={[
                                                { title: 'Attach Files', id: 'a' },
                                                { title: 'Copy from Transaction', id: 'c' },
                                                { title: 'Web print / scan', id: 'w' },
                                            ]}
                                        />
                                        {show && <UploadFileModal
                                            tab="record"
                                            onClickSave={uploadFiles}
                                            handleClose={hideModal}
                                            type={docTypes[fileType]}
                                            showModal={show}
                                            allowAppend={showReplaceAppend}
                                            states={props.transactionDetails.facility.billingStates}
                                            defaultState={props.transactionDetails.facility.address.state}
                                        />}
                                        {showCopyTransaction && <CopyTransactionModal
                                            show={showCopyModal}
                                            handleClose={hideCopyModal}
                                            transactionId={props.transactionId}
                                            showCopyTransaction={showCopyTransaction}
                                        />}
                                    </span>
                                }
                            </div>
                        </div>

                        <MedicalRecordsRetrieve
                            documentSources={props.documentSources}
                            getTransactionDocuments={refreshDocuments}
                            deleteDocument={props.deleteDocument}
                            transactionId={props.transactionId}
                            rows={(editingMed ? editRows : props.documentsList).filter(document => document.recType == DocTypes.MED)}
                            getDocumentById={props.getDocumentById}
                            setShowRecordGrid={() => recordGridState(false)}
                            setSelectedMedDocumentIndex={setSelectedMedDocumentIndex}
                            deleteDocumentSuccess={props.deleteDocumentSuccess}
                            minDate={moment(props.patientDob).toDate()}
                            maxDate={new Date()}
                            auditOnShowDoc={false}
                            isEditing={editingMed}
                            changeSubRowData={changeSubRowData}
                            errors={errorsMed}
                            billingStates={billingStates}
                            isMultiState={isMultiState}
                        />
                    </div>
                    :
                    <div className='viewer-files-list-outer'>
                        <div className="viewer-files-list">
                            <span className='upload-items'>
                                <select value={selectedMedDocumentIndex} onChange={(event) => {
                                    setSelectedMedDocumentIndex(event.target.value)
                                }} className="form-control">
                                    <optgroup label="Auxillary">
                                        {auxiliaryRecords.map((file, index) => <option
                                            key={`recordstabaux${index}`}
                                            value={file.id}
                                            id={index.toString()}
                                        >
                                            {file.description || "No file name"}
                                        </option>)}
                                    </optgroup>
                                    <optgroup label="Medical">
                                        {medicalRecords.map((file, index) => <option
                                            key={`recordstabmed${index}`}
                                            value={file.id}
                                            id={index.toString()}
                                        >
                                            {file.description || "No file name"}
                                        </option>)}
                                    </optgroup>
                                </select>
                            </span>
                            <span className='close-Btn'>
                                    <IconWithButton
                                        buttonText={'Save'}
                                        onButtonClick={onClickSave}
                                        hideArrowIcon={true}
                                        isSave={true}
                                        isDisabled={!canSave}
                                    />
                                    <IconWithButton
                                        buttonText={'Close'}
                                        onButtonClick={() => recordGridState(true)}
                                        hideArrowIcon={true}
                                        isCloseIcon={true}/>
                            </span>
                        </div>
                        <PdfViewer
                            key={iframeKey}
                            saveTrigger={saveTrigger}
                            mergeTrigger={false}
                            fileData={medDocumentData()}
                            getFileData={saveFile}
                            canSave={canSave}
                            transactionId={props.transactionId}
                            currentFile={props.currentFile}
                            pageNumber={props.pageNumber}
                            setPageNumber={props.setPageNumber}
                            getAuditTrailData={props.getAuditTrailData!}
                            authorizedEntities={props.authorizedEntities}
                        />
                    </div>
            }
            {props.completeTransactionFinished && <LoadingSpinner/>}
            {showReleaseMissing && <ReleaseDocumentMissingPopUp/>}
            <IntakeResultsDialog
                requestAnalysis={props.requestAnalysis}
                show={showIntakeResults}
                onClose={() => setShowIntakeResults(false)}
                facilityId={props.transactionDetails.facility.id}
                requestBufferId={props.transactionDetails.id}
            />
        </div>
    );

}

function mapStateToProps(state: RootState): ReduxStateProps {
    return {
        copyTransactionComplete: state.dataEntryState.copyTransactionComplete,
        wpsToken: state.dataEntryState.wpsToken,
        formState: state.form,
        completeTransactionFinished: state.dataEntryState.completeTransactionFinished,
        uploadDocumentAPIStatus: state.dataEntryState.uploadDocumentAPIStatus,
        uploadDocumentFailure: state.dataEntryState.uploadDocumentFailure,
        uploadDocumentSuccess: state.dataEntryState.uploadDocumentSuccess,
        featureFlags: state.loginState.featureFlags,
        patientDob: state.dataEntryState.patientDetailsAttributes?.dob,
        feeSchedules: state.dataEntryState.feeSchedules,
        facilityConfiguration: state.dataEntryState.facilityConfiguration,
        requestAnalysis: state.dataEntryState.requestAnalysis,
        getIntakeIntelligenceStatus: state.dataEntryState.getIntakeIntelligenceStatus,
        requestIntakeIntelligenceStatus: state.dataEntryState.requestIntakeIntelligenceStatus,
    }
}

function mapDispatchToProps(dispatch: Dispatch): ReduxDispatchProps {
    return {
        resetTransactionDocumentsToCopy: () => dispatch(resetTransactionDocumentsToCopy()),
        postWPSToken: (transactionId, docType) => dispatch(postWPSToken(transactionId, docType)),
        readBarCodeDDSRequest: (data) => dispatch(readBarCodeDDSRequest(data)),
        getAuditTrailData: (transactionId) => dispatch(getAuditTrail(transactionId)),
        userViewedDocumentRequest: (documentId) => dispatch(userViewedDocumentRequest(documentId)),
        updateReplaceDocument: (data) => dispatch(updateReplaceDocucmentActionCreator(data)),
        batchUpdateDocuments: (data) => dispatch(batchUpdateDocumentsActionCreator(data)),
        requestIntakeIntelligence: (organizationId, requestBufferId, onSuccess) =>
                dispatch(requestIntakeIntelligenceActionCreator(organizationId, requestBufferId, onSuccess)),
    };
}


export default connect(mapStateToProps, mapDispatchToProps)(DataEntryDetailsBody);
